/**
 * The RFC 6962 Merkle tree over the event log, as the registrar computes it
 * (apps/registrar/src/certificate-event/merkle.ts), with WebCrypto: leaf i is
 * the event with sequence number i + 1, hashed as SHA-256(0x00 || canonical
 * JSON of the event as published); an inner node is SHA-256(0x01 || left ||
 * right). Recomputing the root of a tree head from the replayed events, and
 * checking consistency proofs between the tree heads seen, detects a
 * rewritten history, which gap-free numbering alone does not.
 */

import { PublicEvent, canonicalJson } from "./transparency-log";

/** GET <certificate_event_log_tree_head_uri>?tree_size=N */
export interface LogTreeHead {
  log_id: string;
  log_epoch: number;
  hash_algorithm: string;
  tree_size: string;
  /** Hex. */
  root_hash: string;
  /** published_at of the last event in the tree. */
  timestamp?: string;
}

/** GET …/proofs/consistency?first_tree_size=M&second_tree_size=N */
export interface LogConsistencyProof {
  /** Hex hashes. */
  consistency_path: string[];
}

/** Proof that the tree head verified earlier in the session is a prefix. */
export interface TreeHeadConsistency {
  fromSize: string;
  /** The proof as served, for the evidence. */
  link: string;
  /** Undefined when the proof could not be read (see error). */
  valid?: boolean;
  error?: string;
}

/** Result of comparing the tree head with the replayed log (rule R-13). */
export type TreeHeadCheck =
  | {
      status: "verified" | "failed";
      /** The replayed log size the tree head was requested for. */
      treeSize: string;
      head: LogTreeHead;
      /** Root of the tree over the replayed events, hex. */
      computedRoot: string;
      /** The tree head as served, for the evidence. */
      link: string;
      checkedAt: string;
      consistency?: TreeHeadConsistency;
    }
  | { status: "unavailable"; reason: string };

type Hash = Uint8Array<ArrayBuffer>;

export function leafHash(event: PublicEvent): Promise<Hash> {
  return sha256(
    new Uint8Array([0x00]),
    new TextEncoder().encode(canonicalJson(event)),
  );
}

/** MTH(D[n]) over leaf hashes: split at the largest power of two below n. */
export function rootHash(leaves: Hash[]): Promise<Hash> {
  return subtreeHash(leaves, 0, leaves.length);
}

/**
 * Verifies a consistency proof as served (hex hashes) between two tree heads.
 * A malformed proof does not verify.
 */
export async function verifyConsistencyProof(
  first: number,
  second: number,
  firstRoot: string,
  secondRoot: string,
  proof: LogConsistencyProof,
): Promise<boolean> {
  try {
    return await verifyConsistency(
      first,
      second,
      fromHex(firstRoot),
      fromHex(secondRoot),
      proof.consistency_path.map(fromHex),
    );
  } catch {
    return false;
  }
}

/** Verifies a consistency proof (RFC 9162 section 2.1.4.2). */
export async function verifyConsistency(
  first: number,
  second: number,
  firstRoot: Hash,
  secondRoot: Hash,
  proof: Hash[],
): Promise<boolean> {
  if (first === second) {
    return proof.length === 0 && equalBytes(firstRoot, secondRoot);
  }
  if (first === 0 || first > second || proof.length === 0) {
    return false;
  }
  const path = isPowerOfTwo(first) ? [firstRoot, ...proof] : [...proof];
  // Halving instead of >> keeps sizes beyond 2^31 exact.
  let fn = first - 1;
  let sn = second - 1;
  while (fn % 2 === 1) {
    fn = Math.floor(fn / 2);
    sn = Math.floor(sn / 2);
  }
  let fr = path[0];
  let sr = path[0];
  for (const c of path.slice(1)) {
    if (sn === 0) {
      return false;
    }
    if (fn % 2 === 1 || fn === sn) {
      fr = await nodeHash(c, fr);
      sr = await nodeHash(c, sr);
      if (fn % 2 === 0) {
        while (fn % 2 === 0 && fn !== 0) {
          fn = Math.floor(fn / 2);
          sn = Math.floor(sn / 2);
        }
      }
    } else {
      sr = await nodeHash(sr, c);
    }
    fn = Math.floor(fn / 2);
    sn = Math.floor(sn / 2);
  }
  return equalBytes(fr, firstRoot) && equalBytes(sr, secondRoot) && sn === 0;
}

export function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

/** Throws on anything but an even number of hex digits. */
function fromHex(hex: string): Hash {
  if (typeof hex !== "string" || !/^([0-9a-f]{2})*$/i.test(hex)) {
    throw new Error("Not a hex string");
  }
  return new Uint8Array(
    (hex.match(/../g) ?? []).map((pair) => parseInt(pair, 16)),
  );
}

async function subtreeHash(
  leaves: Hash[],
  start: number,
  end: number,
): Promise<Hash> {
  const size = end - start;
  if (size === 0) {
    return sha256();
  }
  if (size === 1) {
    return leaves[start];
  }
  const split = start + largestPowerOfTwoBelow(size);
  const [left, right] = await Promise.all([
    subtreeHash(leaves, start, split),
    subtreeHash(leaves, split, end),
  ]);
  return nodeHash(left, right);
}

function nodeHash(left: Hash, right: Hash): Promise<Hash> {
  return sha256(new Uint8Array([0x01]), left, right);
}

async function sha256(...parts: Uint8Array[]): Promise<Hash> {
  const bytes = new Uint8Array(
    parts.reduce((length, part) => length + part.length, 0),
  );
  let offset = 0;
  for (const part of parts) {
    bytes.set(part, offset);
    offset += part.length;
  }
  return new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
}

function largestPowerOfTwoBelow(n: number): number {
  let k = 1;
  while (k * 2 < n) {
    k *= 2;
  }
  return k;
}

function isPowerOfTwo(n: number): boolean {
  let k = 1;
  while (k < n) {
    k *= 2;
  }
  return n > 0 && k === n;
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  return (
    left.length === right.length &&
    left.every((byte, index) => byte === right[index])
  );
}
